import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import type { User, Provider } from '@supabase/supabase-js';
import type { StudentProfile } from '../types/portal';

const SITE_URL = (import.meta.env.VITE_SITE_URL as string | undefined) ?? 'https://arabicroots.de';

interface StudentAuthState {
  user: User | null;
  profile: StudentProfile | null;
  loading: boolean;
}

interface StudentAuthContextValue extends StudentAuthState {
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, firstName: string, lastName: string) => Promise<{ needsEmailConfirmation: boolean }>;
  signInWithProvider: (provider: Provider) => Promise<void>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<void>;
  resendConfirmation: (email: string) => Promise<void>;
  updatePassword: (newPassword: string) => Promise<void>;
  updateProfile: (updates: Partial<Pick<StudentProfile, 'first_name' | 'last_name' | 'display_name' | 'phone' | 'street' | 'house_number' | 'postal_code' | 'city' | 'country'>>) => Promise<void>;
}

const StudentAuthContext = createContext<StudentAuthContextValue | null>(null);

export const StudentAuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<StudentAuthState>({
    user: null,
    profile: null,
    loading: true,
  });

  useEffect(() => {
    let mounted = true;
    let lastUserId: string | null = null;
    let inFlight: Promise<void> = Promise.resolve();

    const loadProfile = async (user: User | null) => {
      if (!user) {
        lastUserId = null;
        if (mounted) setState({ user: null, profile: null, loading: false });
        return;
      }

      // Skip if we've already loaded this exact user — prevents the
      // getSession() + INITIAL_SESSION double-fire race that could otherwise
      // create two student_profiles rows.
      if (user.id === lastUserId) return;
      lastUserId = user.id;

      const { data } = await supabase
        .from('student_profiles')
        .select('*')
        .eq('id', user.id)
        .maybeSingle();

      let profile = (data as StudentProfile) ?? null;

      if (!profile) {
        const meta = user.user_metadata ?? {};
        const firstName =
          meta.first_name ?? meta.given_name ?? (meta.full_name ?? meta.name ?? '').split(' ')[0] ?? '';
        const lastName =
          meta.last_name ??
          meta.family_name ??
          (meta.full_name ?? meta.name ?? '').split(' ').slice(1).join(' ') ??
          '';
        // Upsert protects against races with concurrent tabs.
        const { data: created } = await supabase
          .from('student_profiles')
          .upsert(
            { id: user.id, first_name: firstName, last_name: lastName, role: 'student', is_active: false },
            { onConflict: 'id', ignoreDuplicates: false },
          )
          .select()
          .maybeSingle();
        profile = (created as StudentProfile) ?? null;
      }

      if (mounted) setState({ user, profile, loading: false });
    };

    const enqueue = (user: User | null) => {
      inFlight = inFlight.then(() => (mounted ? loadProfile(user) : Promise.resolve()));
    };

    // Prime state from the current session, then rely on onAuthStateChange
    // for all future changes.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!mounted) return;
      enqueue(session?.user ?? null);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const nextUser = session?.user ?? null;
      // Defer async work out of the auth callback to prevent the documented
      // Supabase deadlock (never await Supabase calls directly in the callback).
      setTimeout(() => {
        if (!mounted) return;
        enqueue(nextUser);
      }, 0);
    });

    return () => {
      mounted = false;
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
  };

  const signUp = async (email: string, password: string, firstName: string, lastName: string) => {
    const { data, error } = await supabase.auth.signUp({
      email, password,
      options: { data: { first_name: firstName, last_name: lastName }, emailRedirectTo: `${SITE_URL}/login` },
    });
    if (error) throw error;
    return { needsEmailConfirmation: !data.session };
  };

  const signInWithProvider = async (provider: Provider) => {
    const { error } = await supabase.auth.signInWithOAuth({ provider, options: { redirectTo: `${SITE_URL}/dashboard` } });
    if (error) throw error;
  };

  const signOut = async () => { await supabase.auth.signOut(); };

  const resetPassword = async (email: string) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, { redirectTo: `${SITE_URL}/reset-password` });
    if (error) throw error;
  };

  const resendConfirmation = async (email: string) => {
    const { error } = await supabase.auth.resend({ type: 'signup', email, options: { emailRedirectTo: `${SITE_URL}/login` } });
    if (error) throw error;
  };

  const updatePassword = async (newPassword: string) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  };

  const updateProfile = async (updates: Partial<Pick<StudentProfile, 'first_name' | 'last_name' | 'display_name' | 'phone' | 'street' | 'house_number' | 'postal_code' | 'city' | 'country'>>) => {
    if (!state.user) throw new Error('Nicht angemeldet');
    const { error } = await supabase
      .from('student_profiles')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', state.user.id);
    if (error) throw error;
    setState(prev => ({ ...prev, profile: prev.profile ? { ...prev.profile, ...updates } : null }));
  };

  return (
    <StudentAuthContext.Provider value={{ ...state, signIn, signUp, signInWithProvider, signOut, resetPassword, resendConfirmation, updatePassword, updateProfile }}>
      {children}
    </StudentAuthContext.Provider>
  );
};

export const useStudentAuthContext = (): StudentAuthContextValue => {
  const ctx = useContext(StudentAuthContext);
  if (!ctx) throw new Error('useStudentAuthContext must be used inside StudentAuthProvider');
  return ctx;
};
